import React, { createContext, useContext, useEffect, useState } from 'react'
import { Organization } from '@prepro/shared'
import { organizationService } from '@/services/organization'
import { CHOYCE_CREATIVE_ORG_ID, CHOYCE_CREATIVE_ORG_NAME, CHOYCE_CREATIVE_ORG } from '@/config/organization'
import { useAuthStore } from '@/store/auth'

interface OrganizationContextType {
  organization: Organization | null
  currentOrganization: Organization | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined)

const ORG_CACHE_KEY = 'choyce_creative_org_cache'

/**
 * Get cached organization data from localStorage
 * This provides instant loading of org colors/branding on page refresh
 */
function getCachedOrg(): Organization | null {
  try {
    const cached = localStorage.getItem(ORG_CACHE_KEY)
    if (cached) {
      return JSON.parse(cached)
    }
  } catch (e) {
    // Invalid cache, ignore
  }
  return null
}

/**
 * Cache organization data to localStorage
 */
function cacheOrg(org: Organization): void {
  try {
    localStorage.setItem(ORG_CACHE_KEY, JSON.stringify(org))
  } catch (e) {
    // Storage full or disabled, ignore
  }
}

export function OrganizationProvider({ children }: { children: React.ReactNode }) {
  // Initialize with cached org data for instant display, or use hardcoded fallback
  const [organization, setOrganization] = useState<Organization | null>(() => {
    const cached = getCachedOrg()
    if (cached) return cached
    // Return a minimal org object with hardcoded Choyce Creative branding
    // This ensures proper branding even before authentication
    return {
      id: CHOYCE_CREATIVE_ORG_ID,
      name: CHOYCE_CREATIVE_ORG.name,
      colors: CHOYCE_CREATIVE_ORG.colors,
      logoUrl: CHOYCE_CREATIVE_ORG.logoUrl,
      faviconUrl: CHOYCE_CREATIVE_ORG.faviconUrl,
    } as Organization
  })
  const [loading, setLoading] = useState(false) // Never show loading - always have data
  const [error, setError] = useState<string | null>(null)

  // Watch for auth state changes
  const firebaseUser = useAuthStore((state) => state.firebaseUser)

  const fetchOrganization = async () => {
    try {
      setError(null)
      // Always load Choyce Creative organization (single-tenant mode)
      const org = await organizationService.getOrganization(CHOYCE_CREATIVE_ORG_ID)
      if (org) {
        setOrganization(org)
        cacheOrg(org) // Cache for next page load
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load organization')
      // Keep current org (cached or hardcoded) if fetch fails
    } finally {
      setLoading(false)
    }
  }

  // Fetch on mount and when user logs in
  useEffect(() => {
    fetchOrganization()
  }, [firebaseUser?.uid]) // Refetch when user changes (login/logout)

  return (
    <OrganizationContext.Provider
      value={{
        organization,
        currentOrganization: organization,
        loading,
        error,
        refetch: fetchOrganization,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  )
}

export function useOrganization() {
  const context = useContext(OrganizationContext)
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider')
  }
  return context
}